import { IEventLoggingComponentProps } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { ContactFilterCriteriaProperty, IOwnershipFilter } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import Waypoint from 'react-waypoint';
import { IAdminUserSessionComponentProps } from '../../../../../models/AdminModels';
import { UserSessionViewModelKey } from '../../../../../models/AppState';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { OwnershipFilterDropdown } from '../../../../../web/components/OwnershipFilterDropdown';
import { ContactsListItem } from '../../../../../web/components/contacts/ContactsListItem';
import { RecipientsCount } from '../../RecipientsCount';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IAdminUserSessionComponentProps {
	contacts: Api.IContact[];
	filterRequest: Api.IBulkContactsRequest;
	selectedUsers: Api.IUser[];
	getMoreContacts(): void;
	isBusy: boolean;
	emailApproximation: Api.ContactFilterApproximation;
	onFilterChanged(filter: Api.IOwnershipFilter): void;
	impersonationContext: Api.IImpersonationContext;
}

const _BulkEmailApprovalContactsList: React.FC<IProps> = ({
	contacts,
	selectedUsers,
	userSession,
	getMoreContacts,
	isBusy,
	emailApproximation,
	onFilterChanged,
	impersonationContext,
}) => {
	const [selectedFilterOptionOverride, setSelectedFilterOptionOverride] = React.useState<IOwnershipFilter | null>(null);
	const defaultSelectedFilterOption = React.useMemo(() => {
		return (
			selectedFilterOptionOverride?.criteria?.property ??
			(selectedUsers.length === 1 ? Api.ContactFilterCriteriaProperty.OwnedBy : Api.ContactFilterCriteriaProperty.All)
		);
	}, [selectedUsers.length, selectedFilterOptionOverride]);

	const filterOptions = React.useMemo(() => {
		const newFilterOptions =
			userSession.user.ownershipFilters?.filter(x => x.criteria.property === ContactFilterCriteriaProperty.All) ?? [];

		const isStaleFilterOptionSelected =
			selectedUsers.length !== 1 &&
			selectedFilterOptionOverride?.criteria.property === ContactFilterCriteriaProperty.OwnedBy;

		if (isStaleFilterOptionSelected) {
			setSelectedFilterOptionOverride(null);
		}

		if (selectedUsers.length === 1) {
			const ownedBy = {
				title: 'Contacts owned',
				criteria: { property: ContactFilterCriteriaProperty.OwnedBy, value: selectedUsers[0].id },
			};
			newFilterOptions.push(ownedBy);
		}
		return newFilterOptions;

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUsers.length, userSession.user.ownershipFilters]);

	const selectedFilterOption = useMemo(() => {
		const selected = filterOptions.find(x => x.criteria.property === defaultSelectedFilterOption);
		return selected;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultSelectedFilterOption, filterOptions]);

	useEffect(() => {
		onFilterChanged(selectedFilterOption);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultSelectedFilterOption]);

	const onFilterOptionChanged = (selectedOption: IOwnershipFilter) => {
		setSelectedFilterOptionOverride(selectedOption);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.filter)}>
					<div>Showing:</div>
					<OwnershipFilterDropdown
						styles={[styleSheet.filterDropdown]}
						onOwnershipFilterChanged={onFilterOptionChanged}
						allowSelectAUserOption={false}
						filterOptions={filterOptions}
						initialOwnershipFilter={selectedFilterOption}
						selectedUserId={selectedUsers.length === 1 ? selectedUsers[0].id : null}
						impersonationContext={impersonationContext}
					/>
				</div>
				<RecipientsCount
					className={css(styleSheet.count, !contacts.length && styleSheet.countImportant)}
					approximation={emailApproximation}
				/>
			</div>
			<div className={css(styleSheet.list)}>
				{contacts.map(x => (
					<ContactsListItem className={css(styleSheet.listItem)} contact={x} hideCheckbox={true} key={x.id} />
				))}
				{!!isBusy && (
					<div className={css(styleSheet.listItem, styleSheet.listItemLoading)}>
						<LoadingSpinner type='small' />
					</div>
				)}
				{contacts.length > 0 && <Waypoint bottomOffset='-200px' onEnter={getMoreContacts} />}
			</div>
		</div>
	);
};

const BulkEmailApprovalContactsListAsObserver = observer(_BulkEmailApprovalContactsList);
export const BulkEmailApprovalContactsList = inject(UserSessionViewModelKey)(BulkEmailApprovalContactsListAsObserver);
